import { CommonRegistry, InventoryData } from '../components/components';
import { World } from '../World';

/**
 * Interface for inventory operations used by EquipmentService and ItemPossessionService.
 */
export interface InventoryOperations {
  findItemLocation(itemEntityId: number): { inventoryEntityId: number; slotIndex: number } | null;
  removeItem(inventoryEntityId: number, slotIndex: number): number | null;
  addItem(inventoryEntityId: number, itemEntityId: number, slotIndex?: number): number | null;
}

/**
 * Extended inventory operations for ItemPossessionService.
 */
export interface ExtendedInventoryOperations extends InventoryOperations {
  findStackWithSpace(inventoryEntityId: number, itemId: string): number;
  getItemAt(inventoryEntityId: number, slotIndex: number): number | null;
  addToStack(inventoryEntityId: number, slotIndex: number, quantity: number): boolean;
  countEmptySlots(inventoryEntityId: number): number;
}

/**
 * Service for managing entity inventories.
 * Encapsulates all inventory operations to enforce invariants:
 * - An item entity can only exist in one inventory slot at a time
 * - Stack quantities are properly managed
 *
 * This is a common service usable by both client and server.
 * Server-specific operations (e.g., splitStack) are in ServerInventoryService.
 */
export class InventoryService implements ExtendedInventoryOperations {
  protected readonly world: World<CommonRegistry>;

  constructor(world: World<CommonRegistry>) {
    this.world = world;
  }

  /**
   * Add an item entity to an inventory.
   * @param inventoryEntityId - Entity that has the Inventory component
   * @param itemEntityId - Item entity to add
   * @param slotIndex - Optional specific slot (uses first empty if not specified)
   * @returns The slot index used, or null if inventory is full
   * @throws If item is already in another inventory
   */
  addItem(inventoryEntityId: number, itemEntityId: number, slotIndex?: number): number | null {
    // Check if item is already in an inventory
    const existingLocation = this.findItemLocation(itemEntityId);
    if (existingLocation) {
      throw new Error(
        `Item ${itemEntityId} is already in inventory ${existingLocation.inventoryEntityId} ` +
          `at slot ${existingLocation.slotIndex}`,
      );
    }

    const inventory = this.getInventoryComponent(inventoryEntityId);
    if (!inventory) {
      return null;
    }

    // Find target slot
    let targetSlot: number;
    if (slotIndex !== undefined) {
      if (slotIndex < 0 || slotIndex >= inventory.maxSlots) {
        return null;
      }
      // Check if slot is already occupied (handle both allocated and unallocated slots)
      if (slotIndex < inventory.slots.length && inventory.slots[slotIndex] !== null) {
        return null; // Slot occupied
      }
      targetSlot = slotIndex;
    } else {
      // Find first empty slot
      targetSlot = this.findEmptySlot(inventory);
      if (targetSlot === -1) {
        return null; // No empty slots
      }
    }

    // Ensure slots array is large enough
    while (inventory.slots.length <= targetSlot) {
      inventory.slots.push(null);
    }

    inventory.slots[targetSlot] = itemEntityId;
    this.world.updateComponent(inventoryEntityId, inventory, { slots: inventory.slots });
    return targetSlot;
  }

  /**
   * Remove an item from an inventory slot.
   * @param inventoryEntityId - Entity that has the Inventory component
   * @param slotIndex - Slot to remove from
   * @returns The removed item entity ID, or null if slot was empty
   */
  removeItem(inventoryEntityId: number, slotIndex: number): number | null {
    const inventory = this.getInventoryComponent(inventoryEntityId);
    if (!inventory || slotIndex < 0 || slotIndex >= inventory.slots.length) {
      return null;
    }

    const itemEntityId = inventory.slots[slotIndex];
    if (itemEntityId === null) {
      return null;
    }

    inventory.slots[slotIndex] = null;
    this.world.updateComponent(inventoryEntityId, inventory, { slots: inventory.slots });
    return itemEntityId;
  }

  /**
   * Move an item between inventories or slots.
   * Handles swapping if destination slot is occupied.
   * @param fromInventoryId - Source inventory entity
   * @param fromSlot - Source slot index
   * @param toInventoryId - Destination inventory entity
   * @param toSlot - Destination slot index
   * @returns true if move succeeded, false otherwise
   */
  moveItem(
    fromInventoryId: number,
    fromSlot: number,
    toInventoryId: number,
    toSlot: number,
  ): boolean {
    const fromInventory = this.getInventoryComponent(fromInventoryId);
    const toInventory = this.getInventoryComponent(toInventoryId);

    if (!fromInventory || !toInventory) {
      return false;
    }

    // Early return for no-op moves (same slot in same inventory)
    if (fromInventoryId === toInventoryId && fromSlot === toSlot) {
      return true;
    }

    if (fromSlot < 0 || fromSlot >= fromInventory.slots.length) {
      return false;
    }
    if (toSlot < 0 || toSlot >= toInventory.maxSlots) {
      return false;
    }

    const itemToMove = fromInventory.slots[fromSlot];
    if (itemToMove === null) {
      return false;
    }

    // Ensure destination slots array is large enough
    while (toInventory.slots.length <= toSlot) {
      toInventory.slots.push(null);
    }

    const itemAtDestination = toInventory.slots[toSlot];

    // Perform swap (or simple move if destination is empty)
    fromInventory.slots[fromSlot] = itemAtDestination;
    toInventory.slots[toSlot] = itemToMove;

    this.world.updateComponent(fromInventoryId, fromInventory, { slots: fromInventory.slots });
    if (fromInventoryId !== toInventoryId) {
      this.world.updateComponent(toInventoryId, toInventory, { slots: toInventory.slots });
    }

    return true;
  }

  /**
   * Find which inventory (if any) contains a given item entity.
   * @param itemEntityId - The item entity to find
   * @returns Location info, or null if not in any inventory
   */
  findItemLocation(itemEntityId: number): { inventoryEntityId: number; slotIndex: number } | null {
    // Query all entities with Inventory component
    const inventoryEntities = this.world.query(['Inventory']);

    for (const inventoryEntityId of inventoryEntities) {
      const inventory = this.getInventoryComponent(inventoryEntityId);
      if (!inventory) continue;

      const slotIndex = inventory.slots.indexOf(itemEntityId);
      if (slotIndex !== -1) {
        return { inventoryEntityId, slotIndex };
      }
    }

    return null;
  }

  /**
   * Get the item entity at a specific inventory slot.
   * @param inventoryEntityId - Entity with Inventory component
   * @param slotIndex - Slot to check
   * @returns Item entity ID, or null if slot is empty or invalid
   */
  getItemAt(inventoryEntityId: number, slotIndex: number): number | null {
    const inventory = this.getInventoryComponent(inventoryEntityId);
    if (!inventory || slotIndex < 0 || slotIndex >= inventory.slots.length) {
      return null;
    }
    return inventory.slots[slotIndex];
  }

  /**
   * Count empty slots in an inventory.
   */
  countEmptySlots(inventoryEntityId: number): number {
    const inventory = this.getInventoryComponent(inventoryEntityId);
    if (!inventory) return 0;

    // Count nulls in existing slots + unfilled slots up to maxSlots
    const filledSlots = inventory.slots.filter((s) => s !== null).length;
    return inventory.maxSlots - filledSlots;
  }

  /**
   * Get the quantity of a stack, or 1 if item has no Stack component.
   */
  getQuantity(itemEntityId: number): number {
    const stack = this.world.getComponent(itemEntityId, 'Stack');
    return stack?.quantity ?? 1;
  }

  /**
   * Find a slot containing a stack of the given item that acts as a valid merge target.
   * Valid merge target:
   * - Has same itemId (checked via Item component)
   * - Has space in stack (quantity < maxStack)
   * @returns Slot index, or -1 if no suitable stack found
   */
  findStackWithSpace(inventoryEntityId: number, itemId: string): number {
    const inventory = this.getInventoryComponent(inventoryEntityId);
    if (!inventory) return -1;

    for (let i = 0; i < inventory.slots.length; i++) {
      const itemEntityId = inventory.slots[i];
      if (itemEntityId === null) continue;

      const itemData = this.world.getComponent(itemEntityId, 'Item');
      // If item doesn't have Item component or IDs don't match, skip
      if (!itemData || itemData.itemId !== itemId) continue;

      const stack = this.world.getComponent(itemEntityId, 'Stack');
      // If not stackable or full, skip
      if (!stack || stack.quantity >= stack.maxStack) continue;

      // Found a stack with AT LEAST some space
      return i;
    }

    return -1;
  }

  /**
   * Add quantity to an existing stack in a slot.
   * @returns true if successful, false if slot empty or not stackable or full
   */
  addToStack(inventoryEntityId: number, slotIndex: number, quantity: number): boolean {
    const inventory = this.getInventoryComponent(inventoryEntityId);
    if (!inventory || slotIndex < 0 || slotIndex >= inventory.slots.length) return false;

    const itemEntityId = inventory.slots[slotIndex];
    if (itemEntityId === null) return false;

    const stack = this.world.getComponent(itemEntityId, 'Stack');
    if (!stack) return false;

    if (stack.quantity + quantity > stack.maxStack) {
      return false; // Would overflow
    }

    stack.quantity += quantity;
    this.world.updateComponent(itemEntityId, stack, { quantity: stack.quantity });
    return true;
  }

  protected getInventoryComponent(
    entityId: number,
  ): ({ readonly typeName: 'Inventory' } & InventoryData) | undefined {
    return this.world.getComponent(entityId, 'Inventory');
  }

  protected findEmptySlot(inventory: { readonly typeName: 'Inventory' } & InventoryData): number {
    // Check existing slots first
    for (let i = 0; i < inventory.slots.length; i++) {
      if (inventory.slots[i] === null) {
        return i;
      }
    }
    // If slots array isn't full yet, return next index
    if (inventory.slots.length < inventory.maxSlots) {
      return inventory.slots.length;
    }
    return -1;
  }
}
